import React, { useState, useEffect, useCallback } from 'react';
import MainSidebar from 'components/MainSidebar';
import Icon from 'components/AppIcon';
import { useRealtimeSubscription } from '../../hooks/useRealtimeSubscription';
import { EmptyState } from 'components/SkeletonLoader';
import { getLeaderboard, invalidateLeaderboard } from '../../utils/redisCache';

const LeaderboardsPage = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('networth');
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [realtimeStatus, setRealtimeStatus] = useState('idle');
  const [fromCache, setFromCache] = useState(false);

  const fetchLeaderboard = useCallback(async (isManualRefresh = false) => {
    if (isManualRefresh) {
      setRefreshing(true);
      invalidateLeaderboard();
    } else {
      setLoading(true);
    }
    setFetchError(null);
    try {
      const { data, fromCache: cached, error } = await getLeaderboard();
      if (error) throw new Error(error);
      setLeaderboard(data || []);
      setFromCache(!!cached);
    } catch (err) {
      console.error('Leaderboard fetch error:', err);
      setFetchError(err?.message || 'Failed to load leaderboard');
      setLeaderboard([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchLeaderboard(); }, [fetchLeaderboard]);

  const { status: rtStatus } = useRealtimeSubscription(
    'leaderboard-networth',
    { table: 'player_stats', event: 'UPDATE' },
    useCallback(() => {
      invalidateLeaderboard();
      fetchLeaderboard();
    }, [fetchLeaderboard])
  );
  useEffect(() => { setRealtimeStatus(rtStatus); }, [rtStatus]);

  const tabs = [
    { id: 'networth', label: 'Net Worth', icon: 'DollarSign' },
    { id: 'trading', label: 'Trading', icon: 'TrendingUp' },
    { id: 'factions', label: 'Factions', icon: 'Users' },
  ];

  const top3 = leaderboard?.slice(0, 3);
  const rest = leaderboard?.slice(3);

  return (
    <div className="flex h-screen bg-background text-foreground overflow-hidden">
      <MainSidebar isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(p => !p)} />
      <main className="flex-1 overflow-y-auto">
        <div className="p-4 md:p-6 max-w-5xl mx-auto">
          <div className="mb-5 flex items-center justify-between">
            <div>
              <h1 className="text-xl font-heading font-bold text-foreground flex items-center gap-2">
                <Icon name="Trophy" size={24} color="var(--color-accent)" />
                Leaderboards
              </h1>
              <p className="text-xs text-muted-foreground mt-0.5">Global rankings updated in real-time</p>
            </div>
            <div className="flex items-center gap-2">
              {fromCache && (
                <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-primary/10 border border-primary/20">
                  <Icon name="Zap" size={11} className="text-primary" />
                  <span className="text-xs text-primary font-medium">Cached</span>
                </div>
              )}
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full" style={{ background: realtimeStatus === 'connected' ? 'var(--color-success)' : 'var(--color-muted-foreground)' }} />
                <span className="text-xs text-muted-foreground">{realtimeStatus === 'connected' ? 'Live' : 'Connecting...'}</span>
              </div>
              <button onClick={() => fetchLeaderboard(true)} disabled={refreshing}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs text-muted-foreground hover:text-primary hover:bg-primary/10 border border-border transition-all min-h-[36px] disabled:opacity-50">
                <Icon name={refreshing ? 'Loader2' : 'RefreshCw'} size={13} className={refreshing ? 'animate-spin' : ''} />
                {refreshing ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
          </div>

          {fetchError && (
            <div className="card p-3 border-red-500/30 mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name="AlertCircle" size={15} className="text-red-400" />
                <p className="text-red-400 text-sm">{fetchError}</p>
              </div>
              <button onClick={() => fetchLeaderboard()} className="text-xs text-primary hover:underline min-h-[36px] px-2">Retry</button>
            </div>
          )}

          <div className="flex gap-2 mb-5">
            {tabs?.map(tab => (
              <button key={tab?.id} onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all min-h-[36px] ${
                  activeTab === tab?.id ? 'bg-primary text-primary-foreground' : 'bg-card border border-border text-muted-foreground hover:text-primary'
                }`}>
                <Icon name={tab?.icon} size={13} color="currentColor" />
                {tab?.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="grid grid-cols-3 gap-3 mb-5">
              {[0,1,2]?.map(i => (
                <div key={i} className="card p-4 text-center animate-pulse">
                  <div className="h-8 w-8 rounded-full bg-primary/10 mx-auto mb-2" />
                  <div className="h-3 w-16 rounded bg-primary/10 mx-auto mb-1" />
                  <div className="h-5 w-20 rounded bg-success/10 mx-auto" />
                </div>
              ))}
            </div>
          ) : top3?.length > 0 ? (
            <div className="grid grid-cols-3 gap-3 mb-5">
              {top3?.map((player, i) => (
                <div key={player?.username} className={`card p-4 text-center border ${
                  i === 0 ? 'border-warning/40' : i === 1 ? 'border-slate-400/40' : 'border-amber-600/40'
                }`}>
                  <div className="text-2xl mb-1">{player?.badge}</div>
                  <div className="w-8 h-8 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto mb-1">
                    <span className="text-sm font-bold text-primary">{player?.username?.[0]?.toUpperCase()}</span>
                  </div>
                  <p className="font-semibold text-sm text-foreground truncate">{player?.username}</p>
                  <p className="text-xs text-muted-foreground mb-1">Lvl {player?.level}</p>
                  <p className="text-base font-mono font-bold text-success">${(player?.netWorth / 1000000)?.toFixed(2)}M</p>
                </div>
              ))}
            </div>
          ) : null}

          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 10 })?.map((_, i) => (
                <div key={i} className="h-12 rounded-lg bg-card border border-border animate-pulse" />
              ))}
            </div>
          ) : rest?.length === 0 && top3?.length === 0 ? (
            <EmptyState icon="Trophy" title="No rankings yet" description="Be the first to build your empire!" action={() => {}} actionLabel="" />
          ) : (
            <div className="card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-secondary/50">
                      <th className="sticky left-0 bg-secondary/95 px-3 py-2.5 text-left font-caption text-xs text-muted-foreground uppercase w-12">Rank</th>
                      <th className="px-3 py-2.5 text-left font-caption text-xs text-muted-foreground uppercase">Player</th>
                      <th className="px-3 py-2.5 text-right font-caption text-xs text-muted-foreground uppercase">Net Worth</th>
                      <th className="px-3 py-2.5 text-center font-caption text-xs text-muted-foreground uppercase hidden md:table-cell">Level</th>
                      <th className="px-3 py-2.5 text-center font-caption text-xs text-muted-foreground uppercase hidden lg:table-cell">Faction</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rest?.map((player, i) => (
                      <tr key={player?.rank + '-' + i}
                        className={`border-b border-border/50 transition-colors hover:bg-primary/5 ${
                          i % 2 === 0 ? 'bg-card' : 'bg-secondary/20'
                        }`}>
                        <td className="sticky left-0 px-3 py-2.5 font-data text-sm font-bold text-muted-foreground"
                          style={{ background: i % 2 === 0 ? 'var(--color-card)' : 'rgba(var(--color-secondary-rgb), 0.2)' }}>
                          #{player?.rank}
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                              <span className="text-xs font-bold text-primary">{player?.username?.[0]?.toUpperCase()}</span>
                            </div>
                            <span className="font-medium text-foreground text-sm">{player?.username}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-right font-data text-sm font-semibold text-success">
                          ${(player?.netWorth / 1000000)?.toFixed(2)}M
                        </td>
                        <td className="px-3 py-2.5 text-center hidden md:table-cell">
                          <span className="font-caption text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">Lvl {player?.level}</span>
                        </td>
                        <td className="px-3 py-2.5 text-center hidden lg:table-cell">
                          <span className="font-caption text-xs text-muted-foreground">{player?.faction}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default LeaderboardsPage;
